import { useEffect, useMemo, useRef, useState } from "react";
import {
  applyCollaborationOperations,
  type CollaboratorPresence,
  type CollaborationOperation,
} from "@rwaforms/shared";
import { useBuilderStore } from "./useBuilderStore";
import { buildApiUrl, getApiBaseUrl } from "../lib/api-client";
import type { FormSchemaV1 } from "@rwaforms/shared";

// Cookie name must match the server-side session cookie
const SESSION_COOKIE_NAME = "rwaforms_session";

interface CollaborationDocument {
  schema: FormSchemaV1;
  logic?: unknown;
  settings?: unknown;
}

interface CollaborationOptions {
  draftId: string | null;
  enabled: boolean;
  user: {
    userId: string;
    displayName?: string | null;
    color?: string | null;
  } | null;
}

export interface Collaborator {
  clientId: string;
  userId: string;
  displayName: string;
  color: string;
  lastActive: number;
}

export interface CollaborationState {
  status: "idle" | "connecting" | "connected" | "disconnected";
  version: number;
  collaborators: Collaborator[];
}

export function useCollaborationClient(options: CollaborationOptions): CollaborationState {
  const schema = useBuilderStore((state) => state.schema);
  const applyRemoteUpdate = useBuilderStore((state) => state.applyRemoteUpdate);
  const statusRef = useRef<CollaborationState["status"]>("idle");
  const [state, setState] = useState<CollaborationState>({
    status: "idle",
    version: 0,
    collaborators: [],
  });

  const socketRef = useRef<WebSocket | null>(null);
  const versionRef = useRef<number>(0);
  const documentRef = useRef<CollaborationDocument | null>(null);
  const suppressSnapshotRef = useRef<boolean>(false);
  const presenceRef = useRef<Record<string, CollaboratorPresence>>({});
  const clientSeqRef = useRef<number>(1);
  const abortControllerRef = useRef<AbortController | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const snapshotTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const lastPongRef = useRef<number>(Date.now());
  const sessionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_DELAY_MS = 1000;
  const MAX_RECONNECT_DELAY_MS = 30000;
  const SESSION_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
  const PONG_TIMEOUT_MS = 60 * 1000; // 1 minute

  const resolvedColor = useMemo(() => {
    if (!options.user) return undefined;
    return options.user.color ?? generateColorForId(options.user.userId);
  }, [options.user]);

  useEffect(() => {
    if (!options.enabled || !options.draftId || !options.user) {
      setState((prev) => ({
        ...prev,
        status: "idle",
      }));
      return;
    }

    // Cancel any previous connection attempts
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Clean up any existing intervals/timeouts
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (snapshotTimeoutRef.current) {
      clearTimeout(snapshotTimeoutRef.current);
      snapshotTimeoutRef.current = null;
    }

    const wsUrl = resolveWebSocketUrl(`/collaboration/${options.draftId}?draftId=${options.draftId}`);
    const socket = new WebSocket(wsUrl);
    socketRef.current = socket;
    setConnectionStatus("connecting");

    const handleOpen = () => {
      if (abortController.signal.aborted) {
        socket.close();
        return;
      }
      setConnectionStatus("connected");
      const hello = {
        type: "hello",
        userId: options.user!.userId,
        displayName: options.user!.displayName ?? options.user!.userId,
        color: resolvedColor,
        clientVersion: versionRef.current,
      };
      socket.send(JSON.stringify(hello));
    };

    const handleMessage = (event: MessageEvent) => {
      if (abortController.signal.aborted) {
        return;
      }
      try {
        const data = JSON.parse(event.data as string);
        
        // Update last pong time on pong messages
        if (data.type === "pong") {
          lastPongRef.current = Date.now();
        }
        
        handleInboundMessage(data);
      } catch (error) {
        console.error("Failed to parse collaboration message", error);
      }
    };

    const handleClose = (event: CloseEvent) => {
      if (abortController.signal.aborted) {
        return;
      }
      console.log("WebSocket closed:", event.code, event.reason);
      setConnectionStatus("disconnected");
      socketRef.current = null;
      
      // Attempt reconnection if not a normal closure and not aborted
      if (event.code !== 1000 && !abortController.signal.aborted && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        attemptReconnect();
      }
    };
    
    const attemptReconnect = () => {
      if (abortController.signal.aborted) {
        return;
      }
      
      reconnectAttemptsRef.current++;
      const delay = Math.min(
        RECONNECT_DELAY_MS * Math.pow(2, reconnectAttemptsRef.current - 1),
        MAX_RECONNECT_DELAY_MS
      );
      
      reconnectTimeoutRef.current = setTimeout(() => {
        if (abortController.signal.aborted || !options.enabled || !options.draftId || !options.user) {
          return;
        }
        
        // Reset connection state and reconnect
        const wsUrl = resolveWebSocketUrl(`/collaboration/${options.draftId}?draftId=${options.draftId}`);
        const newSocket = new WebSocket(wsUrl);
        socketRef.current = newSocket;
        setConnectionStatus("connecting");
        
        newSocket.addEventListener("open", () => {
          reconnectAttemptsRef.current = 0;
          setConnectionStatus("connected");
          const hello = {
            type: "hello",
            userId: options.user!.userId,
            displayName: options.user!.displayName ?? options.user!.userId,
            color: resolvedColor,
            clientVersion: versionRef.current,
          };
          newSocket.send(JSON.stringify(hello));
        });
        
        newSocket.addEventListener("message", handleMessage);
        newSocket.addEventListener("close", handleClose);
        newSocket.addEventListener("error", handleError);
      }, delay);
    };

    const handleError = (event: Event) => {
      if (abortController.signal.aborted) {
        return;
      }
      console.error("WebSocket error:", event);
      setConnectionStatus("disconnected");
    };

    socket.addEventListener("open", handleOpen);
    socket.addEventListener("message", handleMessage);
    socket.addEventListener("close", handleClose);
    socket.addEventListener("error", handleError);

    // Ping interval with pong timeout check
    pingIntervalRef.current = setInterval(() => {
      if (abortController.signal.aborted) {
        return;
      }
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        const now = Date.now();
        // Check if pong timeout exceeded
        if (now - lastPongRef.current > PONG_TIMEOUT_MS) {
          console.warn("[CollaborationClient] Pong timeout, closing connection");
          socketRef.current.close(1000, "Pong timeout");
          return;
        }
        socketRef.current.send(JSON.stringify({ type: "ping", nonce: Date.now().toString() }));
      }
    }, 30_000);
    
    // Session timeout
    sessionTimeoutRef.current = setTimeout(() => {
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        console.log("[CollaborationClient] Session timeout, closing connection");
        socketRef.current.close(1000, "Session timeout");
      }
    }, SESSION_TIMEOUT_MS);

    return () => {
      abortController.abort();
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      if (snapshotTimeoutRef.current) {
        clearTimeout(snapshotTimeoutRef.current);
        snapshotTimeoutRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
        sessionTimeoutRef.current = null;
      }
      socket.removeEventListener("open", handleOpen);
      socket.removeEventListener("message", handleMessage);
      socket.removeEventListener("close", handleClose);
      socket.removeEventListener("error", handleError);
      if (socketRef.current) {
        try {
          socketRef.current.close(1000, "Component unmounted");
        } catch {
          // ignore
        }
      }
      socketRef.current = null;
      reconnectAttemptsRef.current = 0;
      setConnectionStatus("disconnected");
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    };
  }, [options.enabled, options.draftId, options.user?.userId, resolvedColor]);

  useEffect(() => {
    if (!options.enabled || statusRef.current !== "connected" || !socketRef.current || !options.user) {
      return;
    }
    if (suppressSnapshotRef.current) {
      suppressSnapshotRef.current = false;
      return;
    }

    // Clear any pending snapshot timeout
    if (snapshotTimeoutRef.current) {
      clearTimeout(snapshotTimeoutRef.current);
      snapshotTimeoutRef.current = null;
    }

    snapshotTimeoutRef.current = setTimeout(() => {
      // Check if component unmounted or connection closed
      if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
        return;
      }
      
      const document: CollaborationDocument = {
        ...(documentRef.current ?? {}),
        schema,
      };
      documentRef.current = document;
      const message = {
        type: "snapshot",
        baseVersion: versionRef.current,
        document,
        clientSeq: clientSeqRef.current++,
      };
      try {
        socketRef.current?.send(JSON.stringify(message));
      } catch (error) {
        console.error("Failed to send collaboration snapshot", error);
      }
      snapshotTimeoutRef.current = null;
    }, 600);

    return () => {
      if (snapshotTimeoutRef.current) {
        clearTimeout(snapshotTimeoutRef.current);
        snapshotTimeoutRef.current = null;
      }
    };
  }, [schema, options.enabled, options.user]);

  function setConnectionStatus(value: CollaborationState["status"]): void {
    statusRef.current = value;
    setState((prev) => ({
      ...prev,
      status: value,
    }));
  }

  function updateCollaborators(map: Record<string, CollaboratorPresence>): void {
    presenceRef.current = map;
    const collaborators: Collaborator[] = Object.entries(map).map(([clientId, presence]) => ({
      clientId,
      userId: presence.userId,
      displayName: presence.displayName ?? presence.userId,
      color: presence.color ?? generateColorForId(presence.userId),
      lastActive: presence.lastActive,
    }));
    setState((prev) => ({
      ...prev,
      collaborators,
    }));
  }

  function applyDocument(document: CollaborationDocument, version: number): void {
    documentRef.current = document;
    versionRef.current = version;
    suppressSnapshotRef.current = true;
    applyRemoteUpdate({
      schema: document.schema,
      updatedAt: new Date().toISOString(),
    });
    setState((prev) => ({
      ...prev,
      version,
    }));
  }

  function handleInboundMessage(message: any): void {
    if (!socketRef.current) return;
    switch (message.type) {
      case "welcome": {
        const document = (message.document ?? null) as CollaborationDocument | null;
        if (document) {
          applyDocument(document, message.version ?? 0);
        }
        if (message.presence && typeof message.presence === "object") {
          updateCollaborators(message.presence as Record<string, CollaboratorPresence>);
        }
        break;
      }
      case "resync": {
        const document = (message.document ?? null) as CollaborationDocument | null;
        if (document) {
          applyDocument(document, message.version ?? versionRef.current);
        }
        if (message.presence && typeof message.presence === "object") {
          updateCollaborators(message.presence as Record<string, CollaboratorPresence>);
        }
        break;
      }
      case "op": {
        const currentDocument = documentRef.current;
        if (currentDocument) {
          const nextDocument = applyCollaborationOperations(currentDocument, message.ops as CollaborationOperation[]);
          applyDocument(nextDocument as CollaborationDocument, message.version ?? versionRef.current);
        } else {
          void socketRef.current.send(JSON.stringify({ type: "request_snapshot" }));
        }
        break;
      }
      case "presence": {
        const current = { ...presenceRef.current };
        if (message.presence === null) {
          delete current[message.clientId];
        } else if (typeof message.presence === "object") {
          current[message.clientId] = message.presence as CollaboratorPresence;
        }
        updateCollaborators(current);
        break;
      }
      case "ack": {
        if (typeof message.version === "number") {
          versionRef.current = message.version;
          setState((prev) => ({
            ...prev,
            version: message.version,
          }));
        }
        break;
      }
      case "error": {
        console.warn("Collaboration channel error", message);
        break;
      }
      case "pong":
      case "snapshot":
      default:
        break;
    }
  }

  return state;
}

function resolveWebSocketUrl(path: string): string {
  const base = getApiBaseUrl();
  let wsUrl: string;
  
  if (base) {
    try {
      const url = new URL(path, base.endsWith("/") ? base : `${base}`);
      if (url.protocol === "https:") {
        url.protocol = "wss:";
      } else if (url.protocol === "http:") {
        url.protocol = "ws:";
      }
      wsUrl = url.toString();
    } catch {
      // Fall through to origin-based resolution
      wsUrl = "";
    }
  } else {
    wsUrl = "";
  }
  
  if (!wsUrl) {
    const resolved = buildApiUrl(path);
    if (resolved.startsWith("http")) {
      const url = new URL(resolved);
      url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
      wsUrl = url.toString();
    } else {
      const origin = window.location.origin.replace(/^http/, "ws");
      wsUrl = `${origin}${resolved.startsWith("/") ? "" : "/"}${resolved}`;
    }
  }
  
  // For cross-origin WebSocket connections, include the session token as a query parameter
  // since cookies may not be sent reliably across origins
  const token = getSessionToken();
  if (token) {
    const url = new URL(wsUrl);
    url.searchParams.set("token", token);
    return url.toString();
  }
  
  return wsUrl;
}

function getSessionToken(): string | null {
  const cookies = document.cookie.split(";").map((c) => c.trim());
  for (const cookie of cookies) {
    if (cookie.startsWith(`${SESSION_COOKIE_NAME}=`)) {
      return decodeURIComponent(cookie.slice(SESSION_COOKIE_NAME.length + 1));
    }
  }
  return null;
}

function generateColorForId(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash << 5) - hash + id.charCodeAt(i);
    hash |= 0;
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 70%, 55%)`;
}
